import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_STRING } from '../../../../constants/common'
import {
  EVENT_NAME_CLICK,
  EVENT_NAME_DRAG_END,
  EVENT_NAME_DRAG_OVER,
  EVENT_NAME_DRAG_START,
  EVENT_NAME_DROP,
  EVENT_NAME_KEY_DOWN,
} from '../../../../constants/event'
import {
  isEventKeyBackspace,
  isEventKeyDelete,
  preventDefault,
  stopPropagation,
} from '../../../../utils/events'

import {
  LINKED_FILES_BLOCK_DRAG_DATA_FORMAT,
  LINKED_FILES_BLOCK_DRAG_DATA_VALUE,
  LINKED_FILES_BLOCK_DRAG_EFFECT_MOVE,
  LINKED_FILES_BLOCK_IDS_ATTRIBUTE,
  LINKED_FILES_BLOCK_QUERY,
  LINKED_FILES_ITEM_ID_ATTRIBUTE,
} from './useEditorFileLinksBlock.constants'
import {
  extractAttributeFromHtml,
  findLinkedFilesBlockElement,
  findLinkedFilesItemElement,
  getLinkedFilesInsertAnchorElement,
  getLinksBlockHtml,
  moveLinkedFilesBlock,
  resetLinkedFilesBlockDragState,
} from './useEditorFileLinksBlock.utils'

export function useEditorFileLinksBlock({
  editorRef,
  initiated,
  linkedFiles,
  onChange,
  onClickFile,
  onRemoveFileLinksBlock,
}) {
  const { t } = useTranslation()

  const isSyncingLinksBlockRef = useRef(false)
  const onChangeRef = useRef(onChange)
  const onClickFileRef = useRef(onClickFile)
  const onRemoveFileLinksBlockRef = useRef(onRemoveFileLinksBlock)

  const infoTextRef = useRef(null)
  const draggedBlockRef = useRef(null)
  const isDraggingRef = useRef(false)
  const pendingSyncRef = useRef(false)
  const handleSyncRef = useRef(null)

  function handleSyncFileLinksBlock() {
    const editor = editorRef.current

    if (editor) {
      const body = editor.getBody()

      if (body) {
        if (isDraggingRef.current) {
          pendingSyncRef.current = true

          return
        }

        const existingBlocks = Array.from(
          body.querySelectorAll(LINKED_FILES_BLOCK_QUERY)
        )
        const [existing, ...duplicatedBlocks] = existingBlocks
        const hasLinks = Array.isArray(linkedFiles) && linkedFiles.length > 0

        if (!hasLinks) {
          if (existingBlocks.length > 0) {
            isSyncingLinksBlockRef.current = true
            editor.undoManager.transact(() => {
              existingBlocks.forEach(block => block.remove())
            })
            isSyncingLinksBlockRef.current = false
            onChangeRef.current?.(editor.getContent())
          }

          return
        }

        if (infoTextRef.current === null) {
          infoTextRef.current = t('Email.FileLinksExpire14DaysKey')
        }

        const html = getLinksBlockHtml({
          infoText: infoTextRef.current || EMPTY_STRING,
          linkedFiles,
        })

        if (!html) {
          return
        }

        const nextIds = extractAttributeFromHtml(
          html,
          LINKED_FILES_BLOCK_IDS_ATTRIBUTE
        )
        const prevIds =
          existing?.getAttribute(LINKED_FILES_BLOCK_IDS_ATTRIBUTE) ??
          EMPTY_STRING

        if (!existing || prevIds !== nextIds) {
          isSyncingLinksBlockRef.current = true
          editor.undoManager.transact(() => {
            if (existing) {
              editor.dom.setOuterHTML(existing, html)
            } else {
              const insertAnchorElement =
                getLinkedFilesInsertAnchorElement(body)

              // Insert linked files block before first anchoring element:
              // reply history or signature.
              if (insertAnchorElement) {
                insertAnchorElement.insertAdjacentHTML('beforebegin', html)
              } else {
                body.insertAdjacentHTML('beforeend', html)
              }
            }
          })
          isSyncingLinksBlockRef.current = false
          onChangeRef.current?.(editor.getContent())
        }

        if (duplicatedBlocks.length > 0) {
          isSyncingLinksBlockRef.current = true
          editor.undoManager.transact(() => {
            duplicatedBlocks.forEach(block => block.remove())
          })
          isSyncingLinksBlockRef.current = false
          onChangeRef.current?.(editor.getContent())
        }
      }
    }
  }

  useEffect(() => {
    onChangeRef.current = onChange
    onClickFileRef.current = onClickFile
    onRemoveFileLinksBlockRef.current = onRemoveFileLinksBlock
  }, [onChange, onClickFile, onRemoveFileLinksBlock])

  // `initiated` is in the deps so the sync re-runs once the editor is ready:
  // when linkedFiles are present as initial values, linkedFiles change before
  // TinyMCE finishes initializing (editorRef.current is still null), so the
  // first run is a no-op and the links block would otherwise never be inserted.
  useEffect(() => {
    if (initiated && editorRef.current && !isSyncingLinksBlockRef.current) {
      handleSyncFileLinksBlock()
    }
  }, [editorRef, handleSyncFileLinksBlock, initiated])

  useEffect(() => {
    handleSyncRef.current = handleSyncFileLinksBlock
  }, [handleSyncFileLinksBlock])

  const dragStateRefs = {
    draggedBlockRef,
    handleSyncRef,
    isDraggingRef,
    pendingSyncRef,
  }

  function setup(editor) {
    editor.on(EVENT_NAME_DRAG_START, event => {
      const target = event.target
      const linksBlock = findLinkedFilesBlockElement(target)

      if (!linksBlock) {
        resetLinkedFilesBlockDragState(dragStateRefs)

        return
      }

      isDraggingRef.current = true
      draggedBlockRef.current = linksBlock

      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = LINKED_FILES_BLOCK_DRAG_EFFECT_MOVE
        event.dataTransfer.setData(
          LINKED_FILES_BLOCK_DRAG_DATA_FORMAT,
          LINKED_FILES_BLOCK_DRAG_DATA_VALUE
        )
      }
    })

    editor.on(EVENT_NAME_DRAG_OVER, event => {
      if (draggedBlockRef.current) {
        preventDefault(event)
        stopPropagation(event)

        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = LINKED_FILES_BLOCK_DRAG_EFFECT_MOVE
        }
      }
    })

    editor.on(EVENT_NAME_DROP, event => {
      const draggedBlock = draggedBlockRef.current

      if (draggedBlock) {
        preventDefault(event)
        stopPropagation(event)

        const editorBody = editor.getBody()
        const dropTarget = findLinkedFilesBlockElement(event.target)

        let moved = false

        editor.undoManager.transact(() => {
          moved = moveLinkedFilesBlock({
            cursorClientY: event.clientY,
            draggedLinksBlockElement: draggedBlock,
            editorBodyElement: editorBody,
            targetLinksBlockElement: dropTarget,
          })
        })

        resetLinkedFilesBlockDragState(dragStateRefs)

        if (moved) {
          onChangeRef.current?.(editor.getContent())
        }
      }
    })

    editor.on(EVENT_NAME_DRAG_END, () => {
      resetLinkedFilesBlockDragState(dragStateRefs)
    })

    editor.on(EVENT_NAME_KEY_DOWN, event => {
      const isBackspace = isEventKeyBackspace(event)
      const isDelete = isEventKeyDelete(event)

      if ((isBackspace || isDelete) && onRemoveFileLinksBlockRef.current) {
        const body = editor.getBody()
        const node = editor.selection.getNode()

        if (body && node) {
          let linksBlock = findLinkedFilesBlockElement(node)

          if (!linksBlock) {
            const sibling = isBackspace
              ? node.previousElementSibling
              : node.nextElementSibling

            if (sibling?.matches?.(LINKED_FILES_BLOCK_QUERY)) {
              linksBlock = sibling
            }
          }

          if (linksBlock) {
            preventDefault(event)
            editor.undoManager.transact(() => {
              linksBlock.remove()
              onRemoveFileLinksBlockRef.current?.()
            })
            onChangeRef.current?.(editor.getContent())
          }
        }
      }
    })

    editor.on(EVENT_NAME_CLICK, event => {
      const target = event.target
      const el = findLinkedFilesItemElement(target)

      if (el) {
        const idRaw = el.getAttribute(`${LINKED_FILES_ITEM_ID_ATTRIBUTE}`)
        const id = Number(idRaw)

        if (Number.isFinite(id)) {
          preventDefault(event)
          stopPropagation(event)
          onClickFileRef.current?.(id)
        }
      }
    })
  }

  return { setup }
}
